import { Box, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { LinkType } from "../../graphql/types"
import {
  type FCWC,
  type FC,
  type ResponsiveImageType,
  ResponsiveImage,
  Container,
  Button,
  type ButtonProps,
} from ".."

type BannerCTAType = {
  title: string
  subtitle: string
  image: ResponsiveImageType
  link: LinkType
}

const BannerCTA: FC<BannerCTAType> = ({ title, subtitle, image, link }) => {
  return (
    <BannerCTASection>
      <BannerImageMedia image={image} />
      <BannerCTAContent>
        <Box>
          <BannerCTASubtitle subtitle={subtitle} />
          <BannerCTATitle title={title} />
        </Box>
        <Box>
          <BannerCTAButton url={link?.uri}>{link?.title}</BannerCTAButton>
        </Box>
      </BannerCTAContent>
    </BannerCTASection>
  )
}

export default BannerCTA

/** A sub-component for the section container of the banner CTA */
const BannerCTASection: FCWC = ({ children }) => (
  <Stack
    position="relative"
    alignItems="flex-end"
    justifyContent="center"
    width="100%"
    height={{ xs: 450, md: 700 }}
    sx={{ isolation: "isolate" }}
  >
    {children}
  </Stack>
)

/** A sub-component for the background image */
const BannerImageMedia: FC<{ image: ResponsiveImageType }> = ({ image }) => (
  <>
    <ResponsiveImage
      image={image}
      position="absolute"
      width="100%"
      height="100%"
      top={0}
      left={0}
      bottom={0}
      right={0}
      sx={{ objectFit: "cover" }}
      zIndex={2}
    />
    <Box
      position="absolute"
      width="100%"
      height="100%"
      sx={{
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette["neutral.900"].main, 0)} 0%, ${alpha(
            theme.palette["neutral.900"].main,
            0.4
          )} 100%)`,
        pointerEvents: "none",
      }}
      zIndex={2}
    />
  </>
)

/** A sub-component for the content of the hero content */
const BannerCTAContent: FCWC = ({ children }) => (
  <Container
    display="flex"
    justifyContent={{ xs: "center", md: "flex-end" }}
    pr={{ xs: 0, md: 10 }}
    position="relative"
    zIndex={3}
  >
    <Stack
      width="100%"
      justifyContent="space-between"
      padding={{ xs: 10, md: 15 }}
      maxWidth={{ xs: 310, md: 493 }}
      minHeight={{ xs: 278, md: 400 }}
      bgcolor={(theme) => theme.palette["neutral.100"].main}
      borderRadius={1.5}
      boxShadow={(theme) => `0 0 8px ${alpha(theme.palette["secondary.800"].main, 0.1)}`}
      color={(theme) => theme.palette["neutral.100"].main}
      sx={{ backdropFilter: "blur(4px)" }}
    >
      {children}
    </Stack>
  </Container>
)

/** A sub-component for the banner CTA card title */
const BannerCTATitle: FC<{ title: string }> = ({ title }) => (
  <Box>
    <Typography variant="heading.xl" component="h2" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      {title}
    </Typography>
  </Box>
)

/** A sub-component for the banner CTA card subtitle */
const BannerCTASubtitle: FC<{ subtitle: string }> = ({ subtitle }) => (
  <Box mb={5}>
    <Typography variant="body.md" component="h3" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      {subtitle}
    </Typography>
  </Box>
)

/** A sub-component for the banner CTA card button */
const BannerCTAButton: FC<ButtonProps & { url: string }> = ({ url, ...props }) => (
  <Button
    href={url}
    sx={(theme) => ({
      width: "100%",
      [theme.breakpoints.down("md")]: {
        fontSize: theme.spacing(3),
        padding: theme.spacing(4.5, 7.5),
      },
    })}
    {...props}
  />
)
